import { supabase } from './supabase';

/**
 * What we believe about a repository, with permission.
 *
 * A finding is a problem. A known is the property — one app or many, what it
 * runs on, which packages change a decision. Latest wins per subscriber + repo
 * + kind. See supabase/migrations/20260911200000_cto_repo_knowns.sql.
 */

export interface CtoRepoKnownRow {
  id: string;
  subscriberId: string;
  subscriberName: string | null;
  subscriberEmail: string | null;
  repo: string;
  repoRef: string | null;
  kind: 'stack';
  summaryMd: string;
  facts: Record<string, unknown>;
  source: 'mcp' | 'manual';
  createdAt: string;
  updatedAt: string;
}

function mapKnown(r: Record<string, unknown>): CtoRepoKnownRow {
  const subscriber = r.subscriber as { name?: string; email?: string } | null;
  return {
    id: String(r.id),
    subscriberId: String(r.subscriber_id),
    subscriberName: subscriber?.name ?? null,
    subscriberEmail: subscriber?.email ?? null,
    repo: String(r.repo),
    repoRef: r.repo_ref ? String(r.repo_ref) : null,
    kind: (r.kind as 'stack') ?? 'stack',
    summaryMd: String(r.summary_md),
    facts: (r.facts as Record<string, unknown>) ?? {},
    source: (r.source as 'mcp' | 'manual') ?? 'mcp',
    createdAt: String(r.created_at),
    updatedAt: String(r.updated_at),
  };
}

const SELECT = '*, subscriber:subscribers(name, email)';

export async function fetchAllKnowns(limit = 200): Promise<CtoRepoKnownRow[]> {
  const { data, error } = await supabase
    .from('cto_repo_knowns')
    .select(SELECT)
    .order('updated_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapKnown(r as Record<string, unknown>));
}

export async function fetchKnownsForSubscriber(
  subscriberId: string,
): Promise<CtoRepoKnownRow[]> {
  const { data, error } = await supabase
    .from('cto_repo_knowns')
    .select(SELECT)
    .eq('subscriber_id', subscriberId)
    .order('updated_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapKnown(r as Record<string, unknown>));
}

export function knownSubject(known: CtoRepoKnownRow): string {
  return known.subscriberName ?? known.subscriberEmail ?? 'Unattributed';
}
